import type { ProductCardPayload, ShowProductsMessage } from '@coldpress-voice/shared-types';
import { useEffect } from 'react';
import { iconForCategory } from '../lib/categoryGrid';
import { dimensionsLabel } from '../lib/format';
import { toggleSelection } from '../lib/selection';
import { isOnStand } from '../lib/synergyR11';
import { Icon } from './Icon';
import { NoImageFill } from './NoImageFill';
import { OnStandBadge } from './OnStandBadge';

/**
 * Ordinal badges for deixis (R10 §6.2): ①②③… so Gigi's "the second one" resolves to a tile and
 * the visitor sees the same numbering Gigi does. Enclosed-number glyphs 1–20; falls back to the
 * bare number past 20 (a grid that large is out of scope for a single showcase render).
 */
const ORDINAL_GLYPHS = '①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳';
function ordinalGlyph(i: number): string {
  return ORDINAL_GLYPHS[i] ?? String(i + 1);
}

/**
 * Extension of ProductCardPayload with the booth-awareness cue.
 * `on_stand` (shared-types, exhibited-set-contract.md Butler R6) / `onStand` (R11 handoff-contract
 * camelCase) — read defensively at runtime via `isOnStand`, which accepts either spelling.
 */
type CardWithOnStand = ProductCardPayload & { on_stand?: boolean; onStand?: boolean };

// Finish texture dot colours for the texture tag row on cards
const FINISH_COLOURS: Record<string, string> = {
  hammered: '#B8860B', // warm amber
  brushed: '#A9A9A9', // silver-grey
  antique: '#8B6914', // deep gold
  polished: '#C0C0C0', // bright silver
  textured: '#C4A882', // clay-warm
  matte: '#808080', // stone-grey
};

type Props = {
  cards?: ShowProductsMessage;
  selectedSkus: string[];
  onSelectionChange: (skus: string[]) => void;
  /**
   * P1 — R18 Wave 1.5: called when a card is tapped to SELECT (adds to selection).
   * Emits 'tap-to-select' RTVI message so the server knows what was tapped.
   * Silently no-ops when not connected (useVoice.sendTapToSelect guards the WS).
   * Contract: voice-client-contract.md §4.
   */
  onTapToSelect?: (sku: string, name: string) => void;
  /**
   * P1 — R18 Wave 1.5: called when a card is tapped to DESELECT (removes from selection).
   * Mirror of onTapToSelect for the remove action.
   */
  onTapToDeselect?: (sku: string, name: string) => void;
  /**
   * R10 deixis (§6.2): emitted on EVERY grid render with the current on-screen ordering
   * (`grid-layout {items:[{sku, ordinal}]}`) so the server can resolve "the second one".
   * ordinal is 1-based, matching the ①②③ badges the visitor sees.
   */
  onGridLayout?: (items: Array<{ sku: string; ordinal: number }>) => void;
  /**
   * R10 deixis (§6.2): SKUs the server asked to pulse (`ui.highlight`) as Gigi narrates them.
   * The matching tiles play a brass pulse (motion-token, reduced-motion safe).
   */
  highlightSkus?: string[];
  /**
   * R10 (Round 5 #3): emitted on every selection change with the full shortlist
   * (`shortlist-update {skus}`) so Gigi's context reflects the visitor's tray.
   */
  onShortlistUpdate?: (skus: string[]) => void;
};

/**
 * ShowcasePanel: left-side showcase for the VoiceDemo kiosk.
 * - Cards active: ProductCards grid enhanced with texture tags + tap-to-select.
 * - No cards: a quiet WAITING state (R21 X2) — a single line of copy, no tiles.
 *
 * R21 (X2) — the LEGACY 8-tile hardcoded category grid was REMOVED from this idle state. It was a
 * stale surface that rendered BETWEEN the S1 welcome card and the server-driven 12-category grid
 * (`CategoryGrid`, fed by `ui.categories` → store.categoryGrid, R12 F2). The user's dev-86 screenshot
 * caught it: 8 image-less tiles under a "Tell me what you're sourcing — or browse a category below"
 * header appearing before the real grid with product images. Root cause: on `scene:'browse'` with no
 * `categoryGrid` and no cards yet, `ShowcaseScene`'s default branch fell through to this panel, whose
 * idle return was the hardcoded list — so the transition was S1 → (8 stale tiles) → 12-tile grid.
 *
 * The single source of category tiles is now the server's `ui.categories` payload, rendered by
 * `CategoryGrid` (real DB slugs + representative images + deixis ordinals). This panel no longer owns
 * a category list; while browse is on stage but neither the server grid nor product cards have landed,
 * it shows only the waiting line, so the transition is S1 → (waiting) → the real 12-category grid.
 * The `ShowcaseScene` also re-applies a buffered grid on browse arrival (R17 Q2), so this waiting
 * state is a brief beat, not a dead-end. Voice search + touch on the real grid both still work.
 */
export function ShowcasePanel({
  cards,
  selectedSkus,
  onSelectionChange,
  onTapToSelect,
  onTapToDeselect,
  onGridLayout,
  highlightSkus,
  onShortlistUpdate,
}: Props) {
  const hasCards = !!cards && cards.cards.length > 0;

  if (hasCards) {
    return (
      <EnhancedProductCards
        message={cards}
        selectedSkus={selectedSkus}
        onSelectionChange={onSelectionChange}
        onTapToSelect={onTapToSelect}
        onTapToDeselect={onTapToDeselect}
        onGridLayout={onGridLayout}
        highlightSkus={highlightSkus}
        onShortlistUpdate={onShortlistUpdate}
      />
    );
  }

  // R21 (X2) — the quiet in-call WAITING state. No hardcoded category tiles: the server's
  // `ui.categories` grid (CategoryGrid) is the single source of the browse tiles. This renders for
  // the brief beat after the welcome card advances but before the category grid / product cards land
  // (ShowcaseScene re-applies a buffered grid on browse arrival, so it's transient). A single line —
  // never the stale 8-tile surface the dev-86 screenshot caught.
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="browse-waiting">
      <span className="grid h-11 w-11 place-items-center rounded-full bg-brass-soft text-brass">
        <Icon name="swatchPalette" size={20} />
      </span>
      <p className="text-sm text-subtle max-w-xs leading-relaxed" aria-live="polite">
        Tell me what you&apos;re sourcing and I&apos;ll show you the range.
      </p>
    </div>
  );
}

/** ProductCards enhanced with texture tags + tap-to-select + R10 deixis (ordinals + pulse). */
function EnhancedProductCards({
  message,
  selectedSkus,
  onSelectionChange,
  onTapToSelect,
  onTapToDeselect,
  onGridLayout,
  highlightSkus,
  onShortlistUpdate,
}: {
  message: ShowProductsMessage;
  selectedSkus: string[];
  onSelectionChange: (skus: string[]) => void;
  onTapToSelect?: (sku: string, name: string) => void;
  onTapToDeselect?: (sku: string, name: string) => void;
  onGridLayout?: (items: Array<{ sku: string; ordinal: number }>) => void;
  highlightSkus?: string[];
  onShortlistUpdate?: (skus: string[]) => void;
}) {
  /**
   * R10 deixis (§6.2): emit `grid-layout` on EVERY grid render so the server always knows the
   * current on-screen ordering (SKU → 1-based ordinal, matching the ①②③ badges). Keyed on the
   * display_id + the SKU list so it fires once per distinct render, not on every parent re-render.
   */
  const gridKey = `${message.display_id}:${message.cards.map((c) => c.sku).join(',')}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: fire once per distinct grid (gridKey), not per callback identity.
  useEffect(() => {
    if (!onGridLayout) return;
    onGridLayout(message.cards.map((c, i) => ({ sku: c.sku, ordinal: i + 1 })));
  }, [gridKey]);

  const highlightSet = new Set(highlightSkus ?? []);
  // R28 (6.3) — deixis SPOTLIGHT: when the server highlights any tile(s), the grid enters a "spot"
  // mode that DIMS the non-targets while the target(s) LIFT + ring — the same `ui.highlight` signal,
  // presented as a soft spotlight instead of a lone ring. Presentation only (token classes on the
  // existing highlight set); the deixis-pulse ring is retained underneath for the first-glance cue.
  const spotActive = highlightSet.size > 0;

  /**
   * ONE code path for selecting/deselecting a card — called by BOTH the plus/check control and the
   * product image (R24). Uses the pure `toggleSelection` helper so add/remove + which tap message to
   * emit have a single source of truth, and fires exactly one message per gesture (no double-fire).
   */
  function toggleSku(sku: string, displayName: string) {
    const { next, action } = toggleSelection(selectedSkus, sku);
    onSelectionChange(next);
    // P1 R18 Wave 1.5: emit tap-to-select / tap-to-deselect WS message so the server's
    // handler fires and Gigi knows what was tapped (voice-client-contract.md §4).
    // These no-op when not connected (the guard is in useVoice.ts).
    if (action === 'tap-to-deselect') {
      onTapToDeselect?.(sku, displayName);
    } else {
      onTapToSelect?.(sku, displayName);
    }
    // R10 (Round 5 #3): sync the full shortlist to Gigi's context on every tray change.
    onShortlistUpdate?.(next);
  }

  return (
    <section aria-label="Selected products">
      {/* R28 (6.4) — kiosk-distance density: a min-width-driven auto-fill grid so cards stay legibly
          large at 2–3 ft (min ~15rem/240px) rather than squeezing to two thin columns. R28 (6.3) —
          `.deixis-spot` on the grid dims non-targets while a highlight is active. */}
      <div
        className={`grid gap-4 ${spotActive ? 'deixis-spot' : ''}`}
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(15rem, 1fr))' }}
      >
        {(message.cards as CardWithOnStand[]).map((card, i) => {
          const selected = selectedSkus.includes(card.sku);
          const finishKey = (card.finish ?? '').toLowerCase();
          const dotColour = FINISH_COLOURS[finishKey];
          const displayName = card.name ?? card.description ?? card.sku;
          const highlighted = highlightSet.has(card.sku);
          return (
            <article
              key={card.sku}
              data-testid={`product-card-${card.sku}`}
              className={`reveal deixis-dimmable group flex flex-col overflow-hidden rounded-lg bg-surface ring-1 shadow-elev-rest transition-[transform,box-shadow] duration-2 ease-physical hover:-translate-y-0.5 hover:shadow-elev-lift ${
                highlighted ? 'deixis-pulse deixis-target ring-brass' : ''
              } ${
                message.highlight === card.sku
                  ? 'ring-brass'
                  : selected
                    ? 'ring-brass/60'
                    : 'ring-line'
              }`}
              style={{ animationDelay: `${Math.min(i, 6) * 60}ms` }}
            >
              {/* Product image */}
              <div className="aspect-[16/10] overflow-hidden bg-canvas/40 flex items-center justify-center relative">
                {/* R10 deixis: ordinal badge (①②③…) so "the second one" resolves for both parties. */}
                <span
                  aria-hidden
                  data-testid={`ordinal-${card.sku}`}
                  className="absolute bottom-2 left-2 z-[1] grid h-6 min-w-6 place-items-center rounded-full bg-canvas/70 px-1 text-sm leading-none text-brass ring-1 ring-brass/30 backdrop-blur-sm"
                >
                  {ordinalGlyph(i)}
                </span>
                {/* R24 — the image itself is a select target (the plus is sometimes not visible
                    enough; either the plus OR the image selects the piece). A <button> so it is
                    keyboard-activatable (Enter/Space) and screen-reader announced with the same
                    Select/Deselect label + aria-pressed as the plus. It is a SIBLING of the plus
                    control below (not nested), so a tap on the plus never bubbles into this handler
                    and vice-versa — exactly one tap message fires per gesture (toggleSku). */}
                <button
                  type="button"
                  data-testid={`product-image-${card.sku}`}
                  aria-label={selected ? `Deselect ${displayName}` : `Select ${displayName}`}
                  aria-pressed={selected}
                  onClick={() => toggleSku(card.sku, displayName)}
                  className="absolute inset-0 h-full w-full cursor-pointer focus-visible:outline-offset-[-3px]"
                >
                  {card.image?.url ? (
                    <img
                      src={card.image.url}
                      srcSet={`${card.image.url.split('?')[0]}?w=320 320w, ${card.image.url.split('?')[0]}?w=640 640w`}
                      sizes="(min-width: 640px) 50vw, 100vw"
                      loading="lazy"
                      decoding="async"
                      alt={card.name ?? card.sku}
                      className="h-full w-full object-cover transition-transform duration-3 ease-physical group-hover:scale-[1.03]"
                    />
                  ) : (
                    // R28 (6.1) — the branded weave (never a bare "image coming soon" line); the
                    // piece NAME on the warm-metallic weave reads as intentional, not broken.
                    <NoImageFill
                      name={card.name ?? card.description ?? null}
                      icon={iconForCategory(card.category)}
                      category={card.category}
                      variant="tile"
                    />
                  )}
                </button>
                {/* R24 — selected pieces get a brass inset ring over the media area itself so the
                    selection reads on the IMAGE (not only on the card border), now that the image is
                    a tap target. pointer-events-none so it never intercepts taps; colour/ring only,
                    reduced-motion safe. */}
                {selected && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 z-[1] rounded-t-lg ring-2 ring-inset ring-brass/70"
                  />
                )}
                {/* Booth-awareness cue (R11 #3): quiet hairline+brass "On the stand" badge when the
                    SKU is in the exhibited set. Driven by card.on_stand/onStand from show_products
                    (exhibited-set-contract.md Butler R6; isOnStand accepts either spelling).
                    Re-skinned from the old loud sage pill → design-language treatment (§8/§10). */}
                {isOnStand(card) && <OnStandBadge className="absolute top-2 left-2" />}
                {/* Select toggle — brass ring + checkmark */}
                <button
                  type="button"
                  aria-label={selected ? `Deselect ${displayName}` : `Select ${displayName}`}
                  aria-pressed={selected}
                  onClick={() => toggleSku(card.sku, displayName)}
                  className={`absolute top-2 right-2 grid h-9 w-9 place-items-center rounded-full ring-2 transition-all duration-1 ease-physical ${
                    selected
                      ? 'tick-pop tick-ring bg-brass ring-brass shadow-soft'
                      : 'bg-canvas/80 ring-brass/50 hover:bg-brass-soft hover:ring-brass'
                  }`}
                >
                  {/* R28 (6.2) — selection tick: the disc scale-pops (`.tick-pop`) and a single brass
                      ring pulses out (`.tick-ring`) the moment a piece becomes selected. The `key`
                      swap re-mounts the icon on select so the pop replays each time. Reduced-motion →
                      instant (the classes no-op). */}
                  {selected ? (
                    <Icon key="on" name="check" size={16} className="text-on-accent" />
                  ) : (
                    <Icon key="off" name="plus" size={14} className="text-brass" />
                  )}
                </button>
              </div>

              <div className="flex flex-1 flex-col gap-3 p-4">
                <header className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    {/* R28 (6.1) — name clamps to 2 lines with an ellipsis so a long piece name never
                        shoves the card taller than its neighbours (grid rows stay even at distance). */}
                    <h3 className="line-clamp-2 font-display text-base leading-tight text-ink">
                      {card.name ?? card.description ?? card.sku}
                    </h3>
                    <span className="truncate text-xs uppercase tracking-wide text-subtle">
                      {card.category} · {card.sku}
                    </span>
                  </div>
                </header>

                {/* Specs — editorial, 2 metadata items max (R13 U3): Material + Size. Finish is
                    NOT repeated here — it lives once in the texture-tag row below (the field bug:
                    finish appeared as both a spec row AND the texture chip, user #5a). Size was
                    missing entirely — added from the catalogue's dimension groups. */}
                <ul className="flex flex-col gap-1.5 text-xs text-subtle">
                  {card.material ? (
                    <li className="flex justify-between gap-2 border-b hairline pb-1.5">
                      <span>Material</span>
                      <span className="text-right text-ink">{card.material}</span>
                    </li>
                  ) : null}
                  {dimensionsLabel(card.dimensions) ? (
                    <li className="flex justify-between gap-2 border-b hairline pb-1.5">
                      <span>Size</span>
                      <span className="text-right text-ink">
                        {dimensionsLabel(card.dimensions)}
                      </span>
                    </li>
                  ) : null}
                </ul>

                {/* Texture tag row — the single home for finish (dot swatch + label). */}
                {card.finish ? (
                  <div className="flex items-center gap-2">
                    {dotColour && (
                      <span
                        className="inline-block h-3 w-3 rounded-full ring-1 ring-line shrink-0"
                        style={{ backgroundColor: dotColour }}
                        aria-hidden="true"
                      />
                    )}
                    <span className="text-[11px] text-subtle capitalize">{card.finish} finish</span>
                  </div>
                ) : null}

                <div className="mt-auto border-t hairline pt-2">
                  <span className="text-[10px] text-faint">as catalogued, {card.data_vintage}</span>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
